import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpen, Search, Bot, Radar, Activity, ArrowUpRight, ArrowDownRight, ArrowRight, AlertTriangle, TrendingUp } from "lucide-react";

const PROVIDER_COLORS = { openai: "bg-emerald-600", anthropic: "bg-orange-500", gemini: "bg-blue-600" };
const SEV_STYLES = {
  critical: "bg-red-600 text-white",
  high: "bg-red-400 text-white",
  medium: "bg-pollen text-ink",
  low: "bg-beige text-ink border border-forest/15",
};

function ScoreDial({ score, size = "w-16 h-16 text-2xl" }) {
  const tone = score == null ? "bg-cream text-inkmute border border-forest/15"
    : score >= 80 ? "bg-forest text-cream"
    : score >= 50 ? "bg-pollen text-ink"
    : "bg-red-500 text-white";
  return <div className={`${size} rounded-full flex items-center justify-center font-serif shrink-0 ${tone}`}>{score ?? "—"}</div>;
}

function PillarCard({ icon: Icon, title, score, delta, lines, onOpen, testid }) {
  return (
    <div data-testid={testid} role="button" tabIndex={0} onClick={onOpen}
      className="rounded-xl border border-forest/10 bg-beige p-5 cursor-pointer hover:border-forest/30 transition-colors group">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-clay flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {title}</div>
        <ArrowRight className="w-4 h-4 text-inkmute opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="flex items-center gap-4">
        <ScoreDial score={score} />
        <div className="min-w-0">
          {delta != null && delta !== 0 && (
            <div className={`flex items-center gap-1 text-xs font-medium ${delta > 0 ? "text-forest" : "text-red-600"}`}>
              {delta > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}{Math.abs(delta)} pts vs last run
            </div>
          )}
          {lines.filter(Boolean).map((l, i) => <div key={i} className="text-xs text-inkmute">{l}</div>)}
        </div>
      </div>
    </div>
  );
}

const fmtCompact = (n) => (n == null ? "—" : n >= 10000 ? `${(n / 1000).toFixed(0)}k` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

function TrafficPillarCard({ traffic, onOpen }) {
  const hasData = traffic.configured && traffic.clicks_28d != null && traffic.series.length > 0;
  const tone = !hasData ? "bg-cream text-inkmute border border-forest/15"
    : (traffic.delta_pct ?? 0) < 0 ? "bg-red-500 text-white" : "bg-forest text-cream";
  const maxC = Math.max(...(traffic.series || []).map((d) => d.clicks), 1);
  return (
    <div data-testid="pillar-traffic" role="button" tabIndex={0} onClick={onOpen}
      className="rounded-xl border border-forest/10 bg-beige p-5 cursor-pointer hover:border-forest/30 transition-colors group">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest text-clay flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Traffic</div>
        <ArrowRight className="w-4 h-4 text-inkmute opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="flex items-center gap-4">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center font-serif text-xl shrink-0 ${tone}`}>
          {hasData ? fmtCompact(traffic.clicks_28d) : "—"}
        </div>
        <div className="min-w-0">
          {hasData && traffic.delta_pct != null && traffic.delta_pct !== 0 && (
            <div className={`flex items-center gap-1 text-xs font-medium ${traffic.delta_pct > 0 ? "text-forest" : "text-red-600"}`}>
              {traffic.delta_pct > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}{Math.abs(traffic.delta_pct)}% clicks vs prev 2 wks
            </div>
          )}
          {hasData ? (
            <>
              <div className="text-xs text-inkmute">{fmtCompact(traffic.impressions_28d)} impressions · 28 days</div>
              <div className="text-xs text-inkmute">{traffic.falling.length > 0 ? `${traffic.falling.length} pages falling` : "no falling pages"}</div>
            </>
          ) : (
            <div className="text-xs text-inkmute">{traffic.configured ? "Waiting for first sync" : "Not connected — paste the Google key"}</div>
          )}
        </div>
      </div>
      {hasData && (
        <div className="flex items-end gap-[2px] h-8 mt-3">
          {traffic.series.map((d) => (
            <div key={d.date} className="flex-1 bg-forest/70 rounded-t" style={{ height: `${Math.max(6, (d.clicks / maxC) * 100)}%` }} title={`${d.date}: ${d.clicks} clicks`} />
          ))}
        </div>
      )}
    </div>
  );
}

const STEPS = [
  { title: "Playbook", note: "Registers, strategy & standing rules — the single source of truth", tab: "playbook" },
  { title: "On-page SEO", note: "Audit every page, fix with AI plans, sweep the sitemap", tab: "bulk" },
  { title: "Keywords", note: "Search Console truth: what people actually search", tab: "gsc" },
  { title: "GEO", note: "Robots, llms.txt, schema — can AI engines read you?", tab: "geo" },
  { title: "AEO", note: "Are ChatGPT, Claude & Gemini naming and citing you?", tab: "aeo" },
];

export default function OverviewTab({ goTab }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/seo/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const jump = (tab) => (tab === "aeo" ? navigate("/pollen-dots/aeo") : goTab(tab));

  if (!data) return <div className="text-inkmute text-sm">Loading overview…</div>;
  const { pillars, attention, aeo_series, top_queries, gsc, playbook_docs, weak_pages } = data;

  return (
    <div data-testid="growth-overview">
      {/* Pipeline: how it all fits */}
      <div className="mb-6 rounded-xl border border-forest/15 bg-forest text-cream p-5">
        <div className="text-[10px] uppercase tracking-[0.24em] opacity-80 mb-3">How it all fits — content to citation</div>
        <div className="flex flex-wrap items-stretch gap-2">
          {STEPS.map((s, i) => (
            <div key={s.tab} className="flex items-center gap-2 flex-1 min-w-[150px]">
              <div role="button" tabIndex={0} onClick={() => jump(s.tab)} className="flex-1 rounded-lg bg-cream/10 hover:bg-cream/20 transition-colors p-3 cursor-pointer">
                <div className="text-sm font-medium">{i + 1}. {s.title}</div>
                <div className="text-[11px] opacity-75 leading-snug mt-0.5">{s.note}</div>
              </div>
              {i < STEPS.length - 1 && <ArrowRight className="w-4 h-4 opacity-50 shrink-0 hidden lg:block" />}
            </div>
          ))}
        </div>
      </div>

      {/* Five pillar dials */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
        <PillarCard icon={BookOpen} title="Content Playbook" score={pillars.playbook.score} testid="pillar-playbook"
          lines={[`${pillars.playbook.docs_uploaded}/${pillars.playbook.docs_total} documents live`, `${pillars.playbook.docs_fresh} fresh (≤45 days)`]}
          onOpen={() => jump("playbook")} />
        <PillarCard icon={Search} title="SEO Health" score={pillars.seo.score} testid="pillar-seo"
          lines={[
            pillars.seo.last_sweep ? `Last sweep: ${pillars.seo.last_sweep.pages} pages · ${pillars.seo.last_sweep.weak || 0} weak` : "No sitemap sweep yet",
            `${pillars.seo.single_audits} single audits on file`,
          ]}
          onOpen={() => jump("bulk")} />
        <PillarCard icon={Bot} title="AI Visibility (AEO)" score={pillars.aeo.score} delta={pillars.aeo.delta} testid="pillar-aeo"
          lines={[
            pillars.aeo.score != null ? `${pillars.aeo.citations || 0} citations to your domain` : "No run yet — trigger one",
            `${pillars.aeo.prompts_active} prompts tracked weekly`,
          ]}
          onOpen={() => jump("aeo")} />
        <PillarCard icon={Radar} title="GEO Readiness" score={pillars.geo.score} testid="pillar-geo"
          lines={[
            pillars.geo.score != null ? `${pillars.geo.blocked || 0} AI crawlers blocked` : "No check yet",
            pillars.geo.checked_at ? `Checked ${new Date(pillars.geo.checked_at).toLocaleDateString()}` : null,
          ]}
          onOpen={() => jump("geo")} />
        <TrafficPillarCard traffic={pillars.traffic} onOpen={() => jump("traffic")} />
      </div>

      {/* Attention feed */}
      <div className="rounded-xl border border-forest/10 bg-beige p-5 mb-6" data-testid="attention-feed">
        <div className="text-[10px] uppercase tracking-widest text-clay mb-3 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> Needs attention ({attention.length})
        </div>
        {attention.length === 0 ? (
          <div className="text-sm text-inkmute">Nothing needs attention — every pillar is healthy.</div>
        ) : (
          <div className="space-y-2">
            {attention.map((a, i) => (
              <div key={i} role="button" tabIndex={0} onClick={() => jump(a.tab)} className="flex items-start gap-2 rounded-lg bg-cream border border-forest/10 p-3 cursor-pointer hover:border-forest/30 transition-colors">
                <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full mt-0.5 shrink-0 ${SEV_STYLES[a.severity]}`}>{a.severity}</span>
                <div className="min-w-0">
                  <span className="text-[10px] uppercase tracking-widest text-clay mr-2">{a.area}</span>
                  <span className="text-sm text-ink">{a.text}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-inkmute ml-auto shrink-0 mt-0.5" />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* AEO trend */}
        <div className="rounded-xl border border-forest/10 bg-beige p-5">
          <div className="text-[10px] uppercase tracking-widest text-clay mb-3 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> AI share of voice over time</div>
          {aeo_series.length === 0 ? (
            <div className="text-sm text-inkmute">No completed runs yet.</div>
          ) : (
            <div className="space-y-2.5">
              {aeo_series.map((r, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="text-xs text-inkmute w-28 shrink-0 truncate">{new Date(r.started_at).toLocaleDateString()}</div>
                  <div className="flex-1 flex items-center gap-1 h-5">
                    {["openai", "anthropic", "gemini"].map((p) => {
                      const per = (r.per_provider || {})[p];
                      const rate = per && per.count ? per.brand_mentions / per.count : 0;
                      return <div key={p} className={`h-full rounded ${PROVIDER_COLORS[p]}`} style={{ width: `${Math.max(3, rate * 100)}%`, opacity: rate === 0 ? 0.2 : 1 }} title={`${p}: ${Math.round(rate * 100)}%`} />;
                    })}
                  </div>
                  <div className="text-xs text-ink w-12 text-right font-serif">{Math.round((r.brand_mention_rate || 0) * 100)}%</div>
                </div>
              ))}
              <div className="flex gap-4 pt-1 text-[10px] uppercase tracking-widest text-inkmute">
                {Object.entries(PROVIDER_COLORS).map(([p, c]) => <div key={p} className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded ${c}`} />{p}</div>)}
              </div>
            </div>
          )}
        </div>

        {/* Top queries */}
        <div className="rounded-xl border border-forest/10 bg-beige p-5">
          <div className="text-[10px] uppercase tracking-widest text-clay mb-3">Top search queries {gsc ? `· ${gsc.filename}` : ""}</div>
          {top_queries.length === 0 ? (
            <div className="text-sm text-inkmute">No Search Console data yet — import a CSV in the Search Console tab to see the keywords that actually bring clicks.</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {top_queries.map((q, i) => {
                  const label = q["Top queries"] || q.query || q["Top pages"] || q.page || Object.values(q)[0];
                  return (
                    <tr key={i} className="border-b border-forest/5 last:border-0">
                      <td className="py-1.5 text-ink text-xs truncate max-w-[280px]">{String(label)}</td>
                      <td className="py-1.5 text-right text-xs text-inkmute whitespace-nowrap">{q.Clicks ?? q.clicks ?? ""} clicks · {q.Impressions ?? q.impressions ?? ""} impr.</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Playbook freshness + weak pages strip */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-forest/10 bg-beige p-5">
          <div className="text-[10px] uppercase tracking-widest text-clay mb-3">Playbook freshness</div>
          <div className="flex flex-wrap gap-2">
            {playbook_docs.map((d) => (
              <div key={d.slug} role="button" tabIndex={0} onClick={() => jump("playbook")}
                className={`text-xs px-3 py-1.5 rounded-full cursor-pointer border ${!d.version ? "bg-red-50 border-red-300 text-red-700" : (d.days_since_update ?? 0) > 45 ? "bg-pollen/40 border-pollen text-ink" : "bg-cream border-forest/15 text-ink"}`}>
                {d.title} · {d.version ? `v${d.version}` : "empty"}{d.days_since_update != null ? ` · ${d.days_since_update}d ago` : ""}
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-forest/10 bg-beige p-5">
          <div className="text-[10px] uppercase tracking-widest text-clay mb-3">Weakest pages (last sweep)</div>
          {weak_pages.length === 0 ? (
            <div className="text-sm text-inkmute">No pages under 70 in the last sitemap sweep — strong library.</div>
          ) : (
            <div className="space-y-1.5">
              {weak_pages.map((w, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full font-medium ${w.score < 50 ? "bg-red-500 text-white" : "bg-pollen text-ink"}`}>{w.score}</span>
                  <span className="text-ink truncate">{w.url}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
